import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { tagThemeClass } from './tagTheme';
import { toast } from 'sonner';
import { List, FileDown, Images, Loader2, X, FileText, FileSpreadsheet } from 'lucide-react';
import { SelectionExcelDialog } from './TagSelectionExcel';
import { listItems, listImages } from './TagSavedLists';
import { stonesFor } from '@/lib/tagPricing';
import { extractError } from '@/lib/errors';
import { useTagActions } from '@/lib/tagShortcuts';

const TITLE_KEY = 'tagws_selection_title';
const SUM_KEY = 'tagws_selection_summary';
const ACTIONS = {
  'item-print': { label: 'Print item list', icon: List, run: (l) => listItems(l, 'print') },
  'item-pdf': { label: 'Item list PDF', icon: FileDown, run: (l) => listItems(l, 'pdf') },
  'image-print': { label: 'Print image list', icon: Images, run: (l) => listImages(l, 'print') },
  'image-pdf': { label: 'Image list PDF', icon: FileDown, run: (l) => listImages(l, 'pdf') },
};
const BTN = 'inline-flex h-9 items-center justify-center gap-1 rounded-xl border border-border bg-background text-xs font-semibold text-foreground transition-colors duration-150 hover:bg-secondary disabled:opacity-50';

// Asks for a title (e.g. customer name) before building the item / image list of the selection.
function TitleDialog({ selected, actions, onClose }) {
  const fallback = `Selected pieces (${selected.length})`;
  const [title, setTitle] = useState(() => localStorage.getItem(TITLE_KEY) || '');
  const [busy, setBusy] = useState('');
  const [summary, setSummary] = useState(() => localStorage.getItem(SUM_KEY) !== '0');
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [busy, onClose]);
  const go = async (a) => {
    const name = title.trim() || fallback;
    localStorage.setItem(TITLE_KEY, title.trim());
    localStorage.setItem(SUM_KEY, summary ? '1' : '0');
    setBusy(a);
    try {
      await ACTIONS[a].run({ name, items: selected, rm_mode: 'expanded', stone_rates: stonesFor(selected.map((x) => x.id)), summary });
      onClose();
    } catch (e) { toast.error(extractError(e, e?.message || 'Could not build the list.')); } finally { setBusy(''); }
  };
  return createPortal(
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[300] flex items-end justify-center p-3 sm:items-center`} data-testid="tagws-selection-title-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={() => !busy && onClose()} />
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card p-4 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 font-semibold text-foreground"><FileText size={16} /> List title</h3>
          <button onClick={onClose} disabled={!!busy} aria-label="Close" data-testid="tagws-selection-title-close"><X size={18} /></button>
        </div>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder={fallback}
          onKeyDown={(e) => { if (e.key === 'Enter' && actions.length === 1) go(actions[0]); }}
          className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" data-testid="tagws-selection-title-input" />
        <p className="mt-1.5 text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">Printed at the top of the list, e.g. a customer name. Leave blank for “{fallback}”.</p>
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-foreground" data-testid="tagws-selection-summary-toggle">
          <input type="checkbox" checked={summary} onChange={(e) => setSummary(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="tagws-selection-summary-checkbox" />
          Print the summary at the end
        </label>
        <div className={`mt-4 grid gap-2 ${actions.length > 1 ? 'grid-cols-2' : ''}`}>
          {actions.map((a) => {
            const I = ACTIONS[a].icon;
            return (
              <button key={a} type="button" onClick={() => go(a)} disabled={!!busy} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-60" data-testid={`tagws-selection-title-run-${a}`}>
                {busy === a ? <Loader2 size={14} className="animate-spin" /> : <I size={14} />} {ACTIONS[a].label}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Panel footer: four buttons. Mobile bar: one "Lists" button with all four inside.
export function SelectionExports({ selected, mobile }) {
  useTagActions({ go_home: () => { setExcel(false); setAsk(null); } });
  const [ask, setAsk] = useState(null);
  const [excel, setExcel] = useState(false);
  const dialog = (
    <>
      {ask ? <TitleDialog selected={selected} actions={ask} onClose={() => setAsk(null)} /> : null}
      {excel ? <SelectionExcelDialog selected={selected} onClose={() => setExcel(false)} /> : null}
    </>
  );
  if (mobile) {
    return (
      <>
        <button type="button" onClick={() => setAsk(Object.keys(ACTIONS))} className="inline-flex h-10 items-center gap-1 rounded-lg border border-border px-2.5 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold" data-testid="tagws-selection-export-mobile"><List size={13} /> Lists</button>
        <button type="button" onClick={() => setExcel(true)} aria-label="Excel" className="inline-flex h-10 items-center gap-1 rounded-lg border border-border px-2 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold text-emerald-700" data-testid="tagws-selection-excel-mobile"><FileSpreadsheet size={13} /></button>
        {dialog}
      </>
    );
  }
  return (
    <>
      {Object.entries(ACTIONS).map(([a, { label, icon: I }]) => (
        <button key={a} type="button" onClick={() => setAsk([a])} className={BTN} title={a.startsWith('item') ? 'Photo, jewellery no., purity, gross & net weight — grouped by purity' : 'Photos with weights (and value when prices are on)'} data-testid={`tagws-selection-${a}`}><I size={13} /> {label}</button>
      ))}
      <button type="button" onClick={() => setExcel(true)} className={`${BTN} col-span-2 text-emerald-700`} data-testid="tagws-selection-excel"><FileSpreadsheet size={13} /> Excel</button>
      {dialog}
    </>
  );
}
